"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Panel,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { countWords } from "@/lib/tool-kit/admissions/counts";
import {
  buildStatementOfPurpose,
  SOP_DEFAULT_WORDS,
  SOP_LEVELS,
  type SopLevel,
  type StatementOfPurposeInput,
  sopWordTotal,
} from "@/lib/tool-kit/admissions/statement-of-purpose";

/** English strings. A translated content file overrides these through `ui`. */
export const STATEMENT_OF_PURPOSE_OUTLINE_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  aboutHeading: "Your application",
  levelLabel: "Type of degree",
  levels: {
    research: "PhD or research degree",
    masters: "Master's degree",
  },
  programLabel: "Degree and school",
  programPlaceholder: "PhD in Marine Ecology, University of Lagos",
  wordsLabel: "Target length (words)",
  wordsHint:
    "Use the programme's limit if it gives one — most ask for roughly 500 to 1,000 words, or one to two pages.",
  motivationHeading: "Opening",
  motivationLabel: "What drew you to this field?",
  motivationPlaceholder:
    "One specific moment, project or question — not a general statement about loving the subject.",
  interestsHeading: "Research and academic interests",
  interestsLabel: "What do you want to study, and why?",
  interestsPlaceholder:
    "The question or area, what is already known, and what you would like to find out.",
  preparationHeading: "Preparation",
  preparationLabel: "Experience, courses and skills — one per line",
  preparationPlaceholder:
    "Honours thesis on coral recovery after bleaching\nTwo field seasons surveying reefs\nStatistics course (R, mixed models)",
  fitHeading: "Fit with the programme",
  facultyLabel: "Faculty whose work connects — one per line, “Name — why”",
  facultyPlaceholder:
    "Prof. Ngozi Eze — long-term reef monitoring dataset\nDr. Tunde Bello — community-based fisheries management",
  resourcesLabel: "Labs, courses, data or facilities — one per line",
  resourcesPlaceholder:
    "The coastal field station\nA graduate seminar on resilience ecology",
  goalsHeading: "Goals",
  goalsLabel: "What will you do during the degree, and afterwards?",
  goalsPlaceholder:
    "The work you hope to complete, and the career it leads towards.",
  checkHeading: "Check your finished statement's length",
  checkLabel: "Paste your finished statement (optional)",
  checkHint: "Counted in your browser; nothing is uploaded.",
  wordCount: "one:{count} word|other:{count} words",
  checkOver: "{count} over your target of {target}.",
  checkUnder: "{count} under your target of {target}.",
  checkOn: "Exactly on your target of {target}.",
  checkNoTarget: "Set a target length above to compare.",
  fileName: "statement-of-purpose-outline",
  document: {
    heading: "Your statement of purpose outline",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every section has your content.",
  },
  defaultTitle: "Statement of Purpose",
  subtitles: {
    research: "Statement of purpose outline · research degree",
    masters: "Statement of purpose outline · master's degree",
  },
  subtitleWords: "{level} · {words} words",
  budgetHeading: "Word Budget",
  budgetNote:
    "A typical split, not a rule. The programme's own prompt and limit come first; if it asks different questions, answer those and use this as a pacing guide.",
  budget: {
    section: "Section",
    share: "Share",
    words: "Target words",
    total: "Total",
  },
  sections: {
    motivation: "1. Opening",
    interests: "2. Research and Academic Interests",
    preparation: "3. Preparation",
    fit: "4. Fit with the Programme",
    goals: "5. Goals",
  },
  target: "Target: about {words} words ({share}).",
  targetShare: "Target: about {share} of the statement.",
  tips: {
    motivation:
      "Start with a specific moment or question. Keep it short — the reader wants your interests next.",
    interests:
      "Be precise about the question, not just the area. Show you know what has been done and what is open.",
    preparation:
      "Choose the two or three experiences that best prepared you, and say what you did and learned in each. Do not repeat your CV.",
    fit: "Name real people and resources, and say why each connects to your plan. Check every name and detail on the programme's site.",
    goals:
      "Say what you will do in the degree and where it leads. Specific and realistic beats ambitious and vague.",
  },
  subheadings: {
    faculty: "Faculty",
    resources: "Resources",
  },
  prompts: {
    motivation:
      "Describe one specific moment, project or question that drew you to this field.",
    interests:
      "State the question you want to work on, what is known about it, and what you would like to find out.",
    preparation:
      "List the experiences, courses and skills that prepared you, and what you did and learned in each.",
    fit: "Explain why this programme, naming faculty and resources.",
    goals:
      "Say what you hope to accomplish during the degree and what you plan to do afterwards.",
    faculty:
      "Name one to three faculty members whose current work relates to yours, and say why — after reading something they have published.",
    resources:
      "Name the labs, courses, data sets or facilities that you would use.",
  },
  facultyHead: {
    name: "Faculty member",
    connection: "Why their work connects",
  },
  checklistHeading: "Before You Submit",
  checklist: [
    "I answered the programme's own prompt and followed its length limit.",
    "Every faculty member and resource I named is current, and I have read their work.",
    "Each experience shows what I did and learned, not just that I was there.",
    "Nothing repeats my CV line for line.",
    "I wrote this version for this programme, not for a list of them.",
    "I proofread it aloud and someone else read it.",
  ],
  example: {
    program: "PhD in Marine Ecology, University of Lagos",
    words: "800",
    motivation:
      "I first understood how fast a reef can change when I dived the same site in Badagry two seasons running and found half of it bleached white.",
    interests:
      "I want to study why some coral colonies recover after bleaching while neighbours do not. Existing work links recovery to temperature history, but little of it separates the role of symbiont type from local water quality in West African reefs.",
    preparation:
      "Honours thesis on coral recovery after bleaching, using photo-quadrat surveys\nTwo field seasons surveying reefs with a monitoring team\nStatistics course covering mixed-effects models in R",
    faculty:
      "Prof. Ngozi Eze — long-term reef monitoring dataset\nDr. Tunde Bello — community-based fisheries management",
    resources:
      "The coastal field station\nA graduate seminar on resilience ecology",
    goals:
      "During the degree I plan to complete a three-year recovery study with a field and a modelling component. Afterwards I hope to lead a monitoring programme for the Gulf of Guinea.",
  },
} as const;

const EMPTY: StatementOfPurposeInput = {
  level: "research",
  program: "",
  words: String(SOP_DEFAULT_WORDS),
  motivation: "",
  interests: "",
  preparation: "",
  faculty: "",
  resources: "",
  goals: "",
};

type TextKey = Exclude<keyof StatementOfPurposeInput, "level">;

export default function StatementOfPurposeOutlineWidget() {
  const strings = useToolUi(STATEMENT_OF_PURPOSE_OUTLINE_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<StatementOfPurposeInput>(EMPTY);
  const [finished, setFinished] = useState("");
  const doc = useMemo(
    () => buildStatementOfPurpose(input, strings, fmt),
    [input, strings, fmt]
  );
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const field = (key: TextKey, rows: number, id: string) => ({
    id: `sop-${id}`,
    onChange: update(key),
    prose: true as const,
    rows,
    value: input[key],
  });
  const total = sopWordTotal(input.words);
  const written = countWords(finished);
  const words = (count: number) =>
    tPlural(strings.wordCount, locale, count, { count: fmt(count) });

  let comparison: string = strings.checkNoTarget;
  if (total !== null) {
    const gap = written - total;
    const values = { count: words(Math.abs(gap)), target: fmt(total) };
    if (gap > 0) {
      comparison = t(strings.checkOver, values);
    } else if (gap < 0) {
      comparison = t(strings.checkUnder, values);
    } else {
      comparison = t(strings.checkOn, values);
    }
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput({ ...EMPTY, ...strings.example })}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setInput(EMPTY);
            setFinished("");
          }}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.aboutHeading}>
        <PillGroup
          label={strings.levelLabel}
          name="sop-level"
          onChange={(level: SopLevel) =>
            setInput((previous) => ({ ...previous, level }))
          }
          options={SOP_LEVELS.map((value) => ({
            value,
            label: strings.levels[value],
          }))}
          value={input.level}
        />
        <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
          <TextField
            id="sop-program"
            label={strings.programLabel}
            onChange={update("program")}
            placeholder={strings.programPlaceholder}
            value={input.program}
          />
          <TextField
            hint={strings.wordsHint}
            id="sop-words"
            label={strings.wordsLabel}
            numeric
            onChange={update("words")}
            value={input.words}
          />
        </div>
      </Panel>

      <Panel title={strings.motivationHeading}>
        <TextAreaField
          {...field("motivation", 3, "motivation")}
          label={strings.motivationLabel}
          placeholder={strings.motivationPlaceholder}
        />
      </Panel>
      <Panel title={strings.interestsHeading}>
        <TextAreaField
          {...field("interests", 4, "interests")}
          label={strings.interestsLabel}
          placeholder={strings.interestsPlaceholder}
        />
      </Panel>
      <Panel title={strings.preparationHeading}>
        <TextAreaField
          {...field("preparation", 4, "preparation")}
          label={strings.preparationLabel}
          placeholder={strings.preparationPlaceholder}
        />
      </Panel>
      <Panel title={strings.fitHeading}>
        <TextAreaField
          {...field("faculty", 3, "faculty")}
          label={strings.facultyLabel}
          placeholder={strings.facultyPlaceholder}
        />
        <TextAreaField
          {...field("resources", 3, "resources")}
          label={strings.resourcesLabel}
          placeholder={strings.resourcesPlaceholder}
        />
      </Panel>
      <Panel title={strings.goalsHeading}>
        <TextAreaField
          {...field("goals", 3, "goals")}
          label={strings.goalsLabel}
          placeholder={strings.goalsPlaceholder}
        />
      </Panel>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="sop"
        labels={strings.document}
      />

      <Panel title={strings.checkHeading}>
        <TextAreaField
          hint={strings.checkHint}
          id="sop-finished"
          label={strings.checkLabel}
          onChange={setFinished}
          prose
          rows={6}
          value={finished}
        />
        <p aria-live="polite" className={HINT}>
          {words(written)}
          {finished.trim() === "" ? "" : ` — ${comparison}`}
        </p>
      </Panel>
    </div>
  );
}
